/** The notification choices on My settings - ADR-0092 (in-app only) and ADR-0212.
 *
 * **Email is off, and the page says so.** In-app notifications are always on; email notifications are
 * not being sent yet (no sender is set up for this workspace), so the two choices below record what
 * the person wants for when they are. A table lists each kind of notification with its in-app state
 * (always on) and its email state (off): the server stores one email choice and one summary choice per
 * person per workspace, not one per kind, so the table is a statement of fact and the two switches
 * are the only controls. Everything that explains that - what a summary is, that the choice is yours
 * in this workspace only, that password-reset and invitation emails are unchanged - is behind the
 * info control (`infoText.ts`, `account.notifications`).
 *
 * The inbox itself, the work queue and the review queue are Action items now (the bell opens it), so
 * the links and counts that used to sit under this form are gone from it, not moved: a second list of
 * what is waiting beside the first is a number that can disagree with it.
 */

import { useState } from "react";
import type { FormEvent } from "react";

import { useMe } from "../api/auth";
import { useNotificationPreferences, useSaveNotificationPreferences } from "../api/notificationPreferences";
import type { NotificationPreferenceChange, NotificationPreferences } from "../api/notificationPreferences";
import { Loaded, WriteFailed } from "../components/Loaded";
import { GridTable, StatusChip } from "../components/ui";
import type { GridColumn } from "../components/ui";
import own from "./Profile.module.css";
import styles from "./Screen.module.css";

/** The kinds of notification, by the family of the kind the server serves (`NotificationKind`'s part
 * before the dot). In-app is always on and email is off; neither is a stored per-kind choice. */
const KINDS: ReadonlyArray<{ id: string; label: string }> = [
  { id: "approval", label: "Approvals" },
  { id: "task", label: "Tasks" },
  { id: "period", label: "Reporting periods" },
  { id: "filing", label: "Filing deadlines" },
  { id: "source", label: "Data gaps" },
];

const COLUMNS: GridColumn<(typeof KINDS)[number]>[] = [
  { id: "kind", header: "Notification", cell: (row) => row.label },
  {
    id: "app",
    header: "In app",
    cell: () => (
      <StatusChip tone="ok" size="sm">
        On
      </StatusChip>
    ),
  },
  {
    id: "email",
    header: "Email",
    cell: () => (
      <StatusChip tone="neutral" size="sm">
        Off
      </StatusChip>
    ),
  },
];

function PreferenceForm({ preference }: { preference: NotificationPreferences }) {
  const save = useSaveNotificationPreferences();
  const [checked, setChecked] = useState(preference.email_notifications);
  const [digest, setDigest] = useState(preference.email_digest);
  const [saved, setSaved] = useState(false);
  const unchanged = checked === preference.email_notifications && digest === preference.email_digest;

  function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    // Only what differs from what is saved: a write that names a choice the person did not touch
    // would restate it, and the server treats a repeat as no change anyway.
    const change: NotificationPreferenceChange = {};
    if (checked !== preference.email_notifications) change.email_notifications = checked;
    if (digest !== preference.email_digest) change.email_digest = digest;
    save.mutate(change, {
      onSuccess: (next) => {
        setChecked(next.email_notifications);
        setDigest(next.email_digest);
        setSaved(true);
      },
    });
  }

  return (
    <form className={own.form} onSubmit={submit}>
      <label>
        <input
          type="checkbox"
          checked={checked}
          disabled={save.isPending}
          onChange={(event) => {
            setChecked(event.target.checked);
            setSaved(false);
            save.reset();
          }}
        />{" "}
        Receive notification emails for this workspace
      </label>
      <label>
        <input
          type="checkbox"
          checked={digest}
          disabled={save.isPending}
          onChange={(event) => {
            setDigest(event.target.checked);
            setSaved(false);
            save.reset();
          }}
        />{" "}
        Send one summary instead of an email for each item
      </label>
      {preference.email_suppressed_reason ? (
        <p role="alert" data-testid="email-suppressed">
          Notification emails to your address have been stopped because the mail provider reported
          {preference.email_suppressed_reason === "complaint"
            ? " a complaint about a message sent to it."
            : " that a message to it could not be delivered."}{" "}
          Your in-app notifications are not affected. There is not yet a way to clear this yourself;
          tell EmitIQ support if the address is correct.
        </p>
      ) : null}
      <WriteFailed error={save.error} of="your notification preference" />
      {saved && !save.isPending ? (
        <p role="status" className={own.saved}>
          Notification preference saved. No notification email was sent.
        </p>
      ) : null}
      <span>
        <button type="submit" className={styles.primary} disabled={save.isPending || unchanged}>
          {save.isPending ? "Saving…" : "Save notification preference"}
        </button>
      </span>
    </form>
  );
}

/** The table of kinds and the two switches. The section heading and its info control are the page's. */
export function NotificationSettings() {
  const identity = useMe();
  const preference = useNotificationPreferences();
  return (
    <>
      <GridTable
        caption="What you are notified of, and how"
        columns={COLUMNS}
        rows={KINDS}
        rowKey={(row) => row.id}
        rowNumbers={false}
        testId="notification-kinds"
      />
      <Loaded query={preference} of="Your notification preference">
        {(saved) => (
          <PreferenceForm key={`${identity.data?.user_id}:${identity.data?.org_id}`} preference={saved} />
        )}
      </Loaded>
    </>
  );
}
